// src/components/features/brands/forms/BrandVoiceForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Paper,
  Divider,
  Autocomplete,
  useTheme,
  alpha,
  Alert,
  FormHelperText,
} from '@mui/material';
import {
  RecordVoiceOver as VoiceIcon,
  Public as PublicIcon,
  Group as AudienceIcon,
  Language as LanguageIcon,
  Place as LocationIcon,
  Block as TabooIcon,
  Forum as MessageIcon,
} from '@mui/icons-material';

// Import FormSection
import { FormSection } from '../../../design-system/components/forms';

// Tone options
const TONE_OPTIONS = [
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'playful', label: 'Playful' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'inspiring', label: 'Inspiring' },
  { value: 'technical', label: 'Technical' },
];

// Language options
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English Only' },
  { value: 'ar', label: 'Arabic Only' },
  { value: 'bilingual', label: 'Bilingual (English & Arabic)' },
];

// Dialect options
const DIALECT_OPTIONS = [
  { value: 'standard', label: 'Standard Arabic' },
  { value: 'egyptian', label: 'Egyptian Dialect' },
  { value: 'gulf', label: 'Gulf Dialect' },
  { value: 'levantine', label: 'Levantine Dialect' },
  { value: 'na', label: 'Not Applicable' },
];

// Region options
const REGION_OPTIONS = [
  'Egypt',
  'Saudi Arabia',
  'Qatar',
  'UAE',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Global',
  'Other'
];

/**
 * Brand voice & tone form component
 * Handles brand personality, communication style, and regional adaptations
 */
const BrandVoiceForm = ({ 
  brandData, 
  data, // Support for the new data prop pattern
  onChange, 
  onNestedChange,
  setFieldValue, // Added for Formik support
  errors = {},
  touched = {},
  requiredFields = []
}) => {
  const theme = useTheme();
  
  // Support both brandData and data prop patterns
  const formData = data || brandData || {};
  
  // Voice data
  const voice = formData.voice || {
    tone: 'professional',
    personality: [],
    values: [],
    targetAudience: [],
    keyMessages: [],
    tabooTopics: []
  };
  
  // Format errors for better display
  const formattedErrors = {
    ...errors,
    voice: {
      ...(typeof errors.voice === 'object' ? errors.voice : {}),
      tone: errors['voice.tone'],
      personality: errors['voice.personality'],
      values: errors['voice.values'],
      targetAudience: errors['voice.targetAudience'],
      keyMessages: errors['voice.keyMessages'],
      tabooTopics: errors['voice.tabooTopics']
    }
  };

  // Format touched state for validation
  const formattedTouched = {
    ...touched,
    voice: {
      ...(typeof touched.voice === 'object' ? touched.voice : {}),
      tone: touched['voice.tone'],
      personality: touched['voice.personality'],
      values: touched['voice.values'],
      targetAudience: touched['voice.targetAudience'],
      keyMessages: touched['voice.keyMessages'],
      tabooTopics: touched['voice.tabooTopics']
    }
  };
  
  // Handle voice field changes with Formik support
  const handleVoiceChange = (field, value) => {
    if (setFieldValue) {
      setFieldValue(`voice.${field}`, value);
    } else if (onNestedChange) {
      onNestedChange('voice', field, value);
    }
  };
  
  // Handle change for voice tone select with Formik support
  const handleToneChange = (e) => {
    if (setFieldValue) {
      setFieldValue('voice.tone', e.target.value);
    } else {
      handleVoiceChange('tone', e.target.value);
    }
  };
  
  // Determine if fields are required
  const isRequired = (field) => {
    return requiredFields.includes(`voice.${field}`);
  };
  
  // Check for validation errors
  const hasVoiceErrors = Object.keys(formattedErrors.voice || {}).some(
    key => Boolean(formattedErrors.voice[key])
  );
  
  return (
    <FormSection
      title="Brand Voice & Tone"
      description="Define how the brand communicates with its audience"
      icon={<VoiceIcon />}
    >
      {/* Display error alert if validation fails */}
      {hasVoiceErrors && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Please complete all required voice & tone fields
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Tone Selection */}
        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth
            error={Boolean(formattedErrors.voice?.tone) && formattedTouched.voice?.tone}
          >
            <InputLabel id="tone-label">Brand Tone</InputLabel>
            <Select
              labelId="tone-label"
              id="tone"
              value={voice.tone}
              onChange={handleToneChange}
              label="Brand Tone"
              required={isRequired('tone')}
            >
              {TONE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {formattedErrors.voice?.tone && formattedTouched.voice?.tone && (
              <FormHelperText error>{formattedErrors.voice.tone}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          {/* Placeholder for alignment */}
          <Box sx={{ height: 1 }}></Box>
        </Grid>
        
        {/* Personality Traits */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            id="personality-traits"
            options={[]}
            freeSolo
            value={voice.personality || []}
            onChange={(e, newValue) => handleVoiceChange('personality', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Brand Personality Traits"
                placeholder="Add trait and press Enter"
                helperText={
                  formattedErrors.voice?.personality && formattedTouched.voice?.personality 
                    ? formattedErrors.voice.personality 
                    : "e.g., Sophisticated, Innovative, Trustworthy"
                }
                fullWidth
                margin="normal"
                required={isRequired('personality')}
                error={Boolean(formattedErrors.voice?.personality) && formattedTouched.voice?.personality}
              />
            )}
          />
        </Grid>
        
        {/* Brand Values */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            id="brand-values"
            options={[]}
            freeSolo
            value={voice.values || []}
            onChange={(e, newValue) => handleVoiceChange('values', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  color="secondary"
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Brand Values"
                placeholder="Add value and press Enter"
                helperText={
                  formattedErrors.voice?.values && formattedTouched.voice?.values 
                    ? formattedErrors.voice.values 
                    : "e.g., Quality, Sustainability, Innovation"
                }
                fullWidth
                margin="normal"
                required={isRequired('values')}
                error={Boolean(formattedErrors.voice?.values) && formattedTouched.voice?.values}
              />
            )}
          />
        </Grid>
        
        {/* Target Audience */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="target-audience"
            options={[]}
            freeSolo
            value={voice.targetAudience || []}
            onChange={(e, newValue) => handleVoiceChange('targetAudience', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  icon={<AudienceIcon />}
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Audience"
                placeholder="Add audience segment and press Enter"
                helperText={
                  formattedErrors.voice?.targetAudience && formattedTouched.voice?.targetAudience 
                    ? formattedErrors.voice.targetAudience 
                    : "e.g., Millennials, Business executives, Luxury consumers"
                }
                fullWidth
                margin="normal"
                required={isRequired('targetAudience')}
                error={Boolean(formattedErrors.voice?.targetAudience) && formattedTouched.voice?.targetAudience}
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Content Guidelines
          </Typography>
        </Grid>
        
        {/* Key Messages */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Key Messages"
            multiline
            rows={4}
            value={voice.keyMessages?.join('\n') || ''}
            onChange={(e) => {
              const messages = e.target.value
                .split('\n')
                .map(message => message.trim())
                .filter(Boolean);
              handleVoiceChange('keyMessages', messages);
            }}
            fullWidth
            placeholder="Enter each key message on a new line"
            helperText="Core messages that should be consistently communicated"
            InputProps={{
              startAdornment: (
                <MessageIcon color="primary" sx={{ mr: 1, mb: voice.keyMessages?.length > 0 ? 15 : 0 }} />
              ),
            }}
          />
        </Grid>
        
        {/* Taboo Topics */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Taboo Topics & Language"
            multiline
            rows={4}
            value={voice.tabooTopics?.join('\n') || ''}
            onChange={(e) => {
              const topics = e.target.value
                .split('\n')
                .map(topic => topic.trim())
                .filter(Boolean);
              handleVoiceChange('tabooTopics', topics);
            }}
            fullWidth
            placeholder="Enter each taboo topic on a new line"
            helperText="Topics and language to avoid in brand communications"
            InputProps={{
              startAdornment: (
                <TabooIcon color="error" sx={{ mr: 1, mb: voice.tabooTopics?.length > 0 ? 15 : 0 }} />
              ),
            }}
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default BrandVoiceForm;